import type { WebSocketResponse } from "@/types/types";
import { GameCampaigns, isGameCampaignsResponse } from "@/types";
import { useAppStore } from "@/stores";
import { getUserIdCookie } from "@/utils/cookies";
import { BaseHandler } from "./base.handler";
import { WebSocketService } from "../websocket.service";

/** The server's answer to a question asked again within its per-connection interval. */
const TOO_MANY_REQUESTS = "too many requests";

/**
 * `action:game_campaigns` — how the game the overlay is docked beside counts toward each campaign
 * the player is served: in its game selection or not, and at what contribution rate. The legacy
 * overlay got both on its campaigns from the game id in its `configure`; the overlay asks here,
 * per connection, so a lobby widget open beside it is never narrowed to one game. The answer
 * lands in `gameFit`.
 *
 * `gameFit` only ever describes the game being asked about: a question about another game drops
 * the previous answer at once, and an error leaves no answer at all — `null`, which narrows
 * nothing, rather than one game's eligibility applied to another. A refusal for asking too soon
 * is the exception: the answer stays, and the next game poll asks again.
 */
export class GameCampaignsHandler extends BaseHandler {
  private seq = 0;
  /** The newest question's request id: only its answer counts. */
  private latest: string | null = null;
  /** What it asked — `${game}|${campaign keys}` — so an unchanged pair isn't asked twice. */
  private asked: string | null = null;
  /** The game the current `gameFit` describes, if any. */
  private answered: string | null = null;

  canHandle(messageType: string): boolean {
    return messageType === GameCampaigns;
  }

  async handle(message: WebSocketResponse): Promise<void> {
    if (!this.validateMessage(message) || !isGameCampaignsResponse(message)) {
      return;
    }
    // An older answer may describe another game, or a campaign set that has
    // since changed.
    if (message.request_id !== this.latest) return;
    // Refused for asking again too soon (the server takes one a second per connection): keep
    // what we have — `request` already dropped another game's answer — and let the next game
    // poll ask again.
    if (message.message === TOO_MANY_REQUESTS) {
      this.asked = null;
      return;
    }
    if (message.result !== "ok") {
      this.answered = null;
      useAppStore.getState().setGameFit(null);
      return;
    }
    this.answered = message.game ?? null;
    useAppStore
      .getState()
      .setGameFit(
        Object.fromEntries(
          (message.campaigns ?? []).map((fit) => [
            fit.id,
            { eligible: fit.eligible, contributionRate: fit.contribution_rate },
          ])
        )
      );
  }

  /** Ask about `game` against the served campaigns `campaigns` names — once per pair. */
  request(game: string, campaigns: string): void {
    const asked = `${game}|${campaigns}`;
    if (asked === this.asked) return;
    this.asked = asked;
    if (game !== this.answered) {
      this.answered = null;
      useAppStore.getState().setGameFit(null);
    }
    this.latest = `game-${++this.seq}`;
    WebSocketService.getInstance().send(GameCampaigns, {
      player: getUserIdCookie(),
      game,
      request_id: this.latest,
    });
  }

  /** No game to ask about (the player left it, or the overlay unmounted): no answer either. */
  forget(): void {
    if (this.asked === null && this.answered === null) return;
    this.asked = null;
    this.answered = null;
    this.latest = null;
    useAppStore.getState().setGameFit(null);
  }

  /** On disconnect the answer to an in-flight question won't arrive: ask again on reconnect. */
  reset(): void {
    this.asked = null;
    this.latest = null;
  }
}

export const gameCampaignsHandler = new GameCampaignsHandler();
